import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { withTranslation } from 'react-i18next'
import moment from 'moment'
import { connect } from 'react-redux'

import { Column, Headings, Input, List, Modal, Padding } from '../../../..'
import { colors } from '../../../../../constants'
import Channel from './Channel'

export const Suggestions = styled(Input)`
    background: white;
    margin-top: 1px;
    font-size: 15px;
    padding: 5px 25px;
    height: 35px;
    width: 100%;
`

const getLatestDate = (date1, date2) => {
    if (!date2) {
        return date1
    }

    if (!date1) {
        return date2
    }

    return moment(date1).diff(moment(date2)) < 0 ? date1 : date2
}

const getLatestUpdateDateFromMediaOrLayers = (list) =>
    list.reduce((lastUpdatedLayerTemp, { updated_at: layerUpdatedAt, pivot = {} }) => {
        const { updated_at: layerPivotUpdatedAt } = pivot

        if (!layerPivotUpdatedAt) {
            return layerUpdatedAt
        }

        const thisLayerLastUpdate = getLatestDate(layerUpdatedAt, layerPivotUpdatedAt)

        return getLatestDate(thisLayerLastUpdate, lastUpdatedLayerTemp)
    }, null)

class DeviceChannels extends PureComponent {
    static propTypes = {
        channels: PropTypes.arrayOf(PropTypes.shape({})),
        deviceID: PropTypes.string,
        innerRef: PropTypes.shape({}),
        isOpen: PropTypes.bool,
        onClickCancel: PropTypes.func,
        onRemoveChannel: PropTypes.func,
        onSelectChannel: PropTypes.func,
        onTextChange: PropTypes.func,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                value: PropTypes.string,
            })
        ),
        selectedIndex: PropTypes.number,
        t: PropTypes.func.isRequired,
    }

    static defaultProps = {
        channels: [],
        deviceID: '',
        innerRef: null,
        isOpen: false,
        onClickCancel: () => null,
        onRemoveChannel: () => null,
        onSelectChannel: () => null,
        onTextChange: () => null,
        options: [],
        selectedIndex: -1,
    }

    constructor(props) {
        super(props)

        this.ref = React.createRef()
    }

    componentDidUpdate(lastProps) {
        const { isOpen: wasOpen } = lastProps
        const { isOpen } = this.props

        if (isOpen && !wasOpen) {
            this.ref.current.focus()
        }
    }

    /**
     * Render a single channel
     * @param {Number} index List index of the item to render
     */
    renderChannel = (index) => {
        const { channels, onRemoveChannel, t } = this.props
        const channel = channels[index]

        // Cannot find the device
        if (!channel) {
            return t('device-not-found')
        }

        // Get channel's data
        const { name = '', id, playlists = [], updated_at: channelUpdateAt, devices = [] } = channel

        // Get latest update at date
        const updatedAt = playlists.reduce((lastUpdatedPlaylist, { layers, media, updated_at: playlistUpdatedAt }) => {
            // Get latest update date from all layers and playlist-layer association
            const lastUpdatedLayer = getLatestUpdateDateFromMediaOrLayers(layers)

            // Get latest update date from all media and playlist-media association
            const lastUpdatedMedia = getLatestUpdateDateFromMediaOrLayers(media)

            // Get latest update date from media and layers
            const layerOrMediaLastUpdate = getLatestDate(lastUpdatedLayer, lastUpdatedMedia)

            // Get latest update between this playlist last update or its children (media or layers)
            const thisPlayerLatestDate = getLatestDate(layerOrMediaLastUpdate, playlistUpdatedAt)

            // Get the date between this playlist or the past playlists
            return getLatestDate(thisPlayerLatestDate, lastUpdatedPlaylist)
        }, channelUpdateAt)

        // Count channel's number of media
        const totalMedia = playlists.reduce((out, { media = [] }) => out + media.length, 0)

        // Count channel's number of layer
        const totalLayers = playlists.reduce((out, { layers = [] }) => out + layers.length, 0)

        // Get first playlist's media
        const { media: firstPlaylistMediaArray = [] } = playlists[0] || {}

        // Get id of the first playlist's media
        const { id: mediaIDToPlay } = firstPlaylistMediaArray[0] || {}

        return (
            <Channel
                estate={this.props.estate}
                devices={devices.length}
                name={name}
                id={id}
                totalLayers={totalLayers}
                totalMedia={totalMedia}
                mediaIDToPlay={mediaIDToPlay}
                onRemoveChannel={() => onRemoveChannel(index)}
                updatedAt={updatedAt}
            />
        )
    }

    render() {
        const { channels, deviceID, isOpen, onClickCancel, onRemoveChannel, onSelectChannel, onTextChange, options, selectedIndex, t } = this.props

        return (
            <Modal isOpen={isOpen} onClose={onClickCancel}>
                <Padding top bottom right size="sm">
                    <Padding left right size="md">
                        <Column>
                            <Headings.H2 bold>{t('Channels')}</Headings.H2>
                        </Column>
                        <Column right>
                            <Headings.H2 color={colors.blue}>{deviceID}</Headings.H2>
                        </Column>
                    </Padding>
                    <Padding left right size="md">
                        <Padding top size="sm">
                            {channels && !!channels.length && (
                                <List
                                    data={channels}
                                    display="name"
                                    onRemove={onRemoveChannel}
                                    selectedIndex={selectedIndex}
                                    itemRenderer={this.renderChannel}
                                />
                            )}
                            <Suggestions
                                ref={this.ref}
                                type="suggestions"
                                onChange={onSelectChannel}
                                onTextChange={onTextChange}
                                options={options}
                                placeholder="Add channel..."
                            />
                        </Padding>
                    </Padding>
                </Padding>
            </Modal>
        )
    }
}
const mapStateToProps = (state) => {
    return {
        estate: state.app.selectedEstate,
    }
}

export default connect(mapStateToProps)(withTranslation()(DeviceChannels))
